import { useEffect, useState } from 'react';
import { z } from 'zod';

import { showErrorToUser, useForm } from '@pletly/ui';
import { useAuth } from '../../../context';
import { getLastEmail, rememberEmail } from '../../../services/rememberLogin';

const formSchema = z.object({
	emailOrUsername: z.string().trim().nonempty(),
	password: z.string().trim().nonempty(),
});

export const useLogin = () => {
	const [isSending, setIsSending] = useState(false);
	const formMethods = useForm(formSchema);
	const { setValue } = formMethods;

	const { login: signIn, abortLogin } = useAuth();

	const handleLogin: Parameters<typeof formMethods.handleSubmit>['0'] = async ({
		emailOrUsername,
		password,
	}) => {
		if (isSending) {
			return;
		}

		setIsSending(true);

		try {
			await rememberEmail(emailOrUsername);
			const isEmail = emailOrUsername.includes('@');
			await signIn({
				email: isEmail ? emailOrUsername : undefined,
				username: !isEmail ? emailOrUsername : undefined,
				password,
			});
		} catch (error) {
			showErrorToUser(error as Error);
		} finally {
			setIsSending(false);
		}
	};

	const onSubmit = formMethods.handleSubmit(handleLogin);

	useEffect(() => {
		void getLastEmail().then((lastEmail) => {
			if (!lastEmail) {
				return;
			}

			setValue('emailOrUsername', lastEmail);
		});
	}, [setValue]);

	useEffect(() => {
		return () => {
			void abortLogin();
		};
	}, [abortLogin]);

	return { isSending, onSubmit, formMethods };
};
